/**
 * @deprecated Use get_target_cities via targetCities.ts — static centers retained for rollback.
 * Approximate city centers for distance filtering (km from venue).
 */
export const CITY_CENTERS: Record<string, { lat: number; lng: number }> = {
  Zurich: { lat: 47.3769, lng: 8.5417 },
  Geneva: { lat: 46.2044, lng: 6.1432 },
  Basel: { lat: 47.5596, lng: 7.5886 },
  Bern: { lat: 46.948, lng: 7.4474 },
  Berlin: { lat: 52.52, lng: 13.405 },
  Munich: { lat: 48.1351, lng: 11.582 },
  Paris: { lat: 48.8566, lng: 2.3522 },
  London: { lat: 51.5074, lng: -0.1278 },
  Amsterdam: { lat: 52.3676, lng: 4.9041 },
  Barcelona: { lat: 41.3874, lng: 2.1686 },
  Madrid: { lat: 40.4168, lng: -3.7038 },
  Rome: { lat: 41.9028, lng: 12.4964 },
  Milan: { lat: 45.4642, lng: 9.19 },
  Lisbon: { lat: 38.7223, lng: -9.1393 },
  Copenhagen: { lat: 55.6761, lng: 12.5683 },
  Stockholm: { lat: 59.3293, lng: 18.0686 },
  Vienna: { lat: 48.2082, lng: 16.3738 },
  Prague: { lat: 50.0755, lng: 14.4378 },
  Dublin: { lat: 53.3498, lng: -6.2603 },
  "New York": { lat: 40.7128, lng: -74.006 },
  "Los Angeles": { lat: 34.0522, lng: -118.2437 },
  Tokyo: { lat: 35.6762, lng: 139.6503 },
  Singapore: { lat: 1.3521, lng: 103.8198 },
  Sydney: { lat: -33.8688, lng: 151.2093 },
  Dubai: { lat: 25.2048, lng: 55.2708 },
};

const EARTH_RADIUS_KM = 6371;

export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistanceKm(km: number): string {
  if (km < 1) return `${(km * 1000).toFixed(0)} m`;
  return `${km.toFixed(1)} km`;
}

export function getCityCenter(city: string): { lat: number; lng: number } | null {
  return CITY_CENTERS[city] ?? null;
}

const COORD_EPSILON = 0.0001;

/** True when stored coords match the city-center fallback (not a real venue location). */
export function isCityCenterFallbackCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
  city: string,
): boolean {
  if (lat == null || lng == null) return false;
  const center = getCityCenter(city);
  if (!center) return false;
  return (
    Math.abs(lat - center.lat) < COORD_EPSILON && Math.abs(lng - center.lng) < COORD_EPSILON
  );
}

/** Map lat/lng into 8–92% for the stylized SVG map. */
export function mapCoords(
  lat: number,
  lng: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
): { x: number; y: number } {
  const { minLat, maxLat, minLng, maxLng } = bounds;
  const x = maxLng === minLng ? 50 : ((lng - minLng) / (maxLng - minLng)) * 84 + 8;
  const y = maxLat === minLat ? 50 : ((maxLat - lat) / (maxLat - minLat)) * 84 + 8;
  return { x: Math.min(92, Math.max(8, x)), y: Math.min(92, Math.max(8, y)) };
}
